import { useState, useEffect, useRef } from 'react';

/**
 * Returns a live countdown string (e.g., "3h 45m 12s") given a UNIX epoch timestamp (seconds).
 * Ticks every second. Returns null if no time is provided or the time has passed.
 */
export function useRestartCountdown(scheduledTimeUnix: number | null): string | null {
  const [countdown, setCountdown] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!scheduledTimeUnix) {
      setCountdown(null);
      return;
    }

    const targetMs = scheduledTimeUnix * 1000;

    const compute = () => {
      const diffMs = targetMs - Date.now();

      if (diffMs <= 0) {
        setCountdown(null);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setCountdown(`${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${seconds}s`);
      }
    };

    compute();
    intervalRef.current = setInterval(compute, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [scheduledTimeUnix]);

  return countdown;
}
